/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProfileConfig.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import DefaultProfilePic from "./../../assets/logoIcons/defaultProfilePic.png";
import { toast } from "react-toastify";
import { useCallback, useEffect, useRef, useState } from "react";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import proileActions from "../../redux/actions/profile";
import customConsole from "../../config/customConsole";
import moment from "moment";
import { userManagementApis } from "../../redux/actions";
import {
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Select,
  MenuItem,
  Divider,
  Popover,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { componentStyle } from "../../config/appTheme";

function ProfileConfig() { // NOSONAR
  const profileDetails = useSelector((store) => store.profileReducer);
  const countryRegionsStore = useSelector(
    (store) => store.countryAndPhoneCodeReducer,
  );

  const dispatch = useDispatch();
  const [isProfilePicUploaded, setIsProfilePicUploaded] = useState(false);
  const [profilePictPath, setProfilePictPath] = useState("");
  const [profilePicFileName, setProfilePicFileName] = useState("");
  const [openProfilePicDialog, setOpenProfilePicDialog] = useState(false);
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const uploadIntervalRef = useRef(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editedPhone, setEditedPhone] = useState("");
  const [editedPhoneCode, setEditedPhoneCode] = useState("");
  const [isEditingTimeZone, setIsEditingTimeZone] = useState(false);
  const [editedTimeZone, setEditedTimeZone] = useState("");
  const isViewingName = isEditingName === false;
  const isViewingPhone = isEditingPhone === false;
  const isViewingTimeZone = isEditingTimeZone === false;


  const [openUserChangeLogsDialog, setOpenUserChangeLogsDialog] =
    useState(false);
  const [userChangeLogsList, setUserChangeLogsList] = useState([]);
  const [userLogsDialogData, setUserLogsDialogData] = useState({});
  const [tabValue, setTabValue] = useState(0);

  const [logsPage, setLogsPage] = useState(1);
  const logsRowsPerPage = 25;

  const [logsDateRange, setLogsDateRange] = useState([null, null]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);

  const compactDatePickerSx = {
    width: "120px !important",
    height: "250px !important",
    "& .MuiPickersLayout-root": {
      minWidth: "0 !important",
      maxWidth: "120px !important",
    },
    "& .MuiPickersLayout-contentWrapper": {
      padding: 0,
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      display: "none",
    },
    "& .MuiPickersCalendarHeader-root": {
      position: "relative",
      paddingLeft: "1px",
      paddingRight: "1px",
      justifyContent: "space-between",
      minHeight: "20px",
      marginBottom: 0,
    },
    "& .MuiPickersCalendarHeader-labelContainer": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "14px",
    },
    "& .MuiPickersArrowSwitcher-root": {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    "& .MuiPickersArrowSwitcher-spacer": {
      display: "none",
    },
    "& .MuiPickersArrowSwitcher-button": {
      padding: 0,
    },
    "& .MuiDayCalendar-header": {
      gap: "0px",
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekContainer": {
      margin: "0px",
      justifyContent: "space-between",
    },
    "& .MuiPickersDay-root": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#000000",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root:not(.Mui-selected)": {
      borderRadius: "0px !important",
    },
    "& .MuiPickersYear-yearButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersMonth-monthButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
  };

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_roles_list_main,
        params: {
          page_number: 1,
          page_size: 100,
          sort_column: "created_at",
          sort_order: "ASC",
        },
        show_toast: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...proileActions.get_profile_details,
        show_toast: false,
        params: {},
      }),
    );
    dispatch(
      HandleApiActions({
        ...proileActions.get_coutry_regions,
        show_toast: false,
      }),
    );
  }, [dispatch]);
  useEffect(() => {
    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
    };
  }, []);

  const handleGetProfileDetails = useCallback(() => {
    return dispatch(
      HandleApiActions({
        ...proileActions.get_profile_details,
        show_toast: false,
        params: {},
      }),
    );
  }, [dispatch]);

  const handleUpdateProfileDetails = useCallback(
    async (data) => {
      const response = await dispatch(
        HandleApiActions({
          ...proileActions.update_profile_details,
          show_toast: true,
          params: data,
        }),
      );

      if (response?.error) return;
      await handleGetProfileDetails();
    },
    [dispatch, handleGetProfileDetails],
  );

  useEffect(() => {
    if (profileDetails?.error) {
      if (isProfilePicUploaded) setIsProfilePicUploaded(false);
      return;
    }

    if (isProfilePicUploaded && profileDetails?.profile_pic_name?.length) {
      customConsole("Prodile pic uploaded, update the profile");
      customConsole(profileDetails?.profile_pic_name);
      setIsProfilePicUploaded(false);
      handleUpdateProfileDetails({
        profile_pic: profileDetails?.profile_pic_name,
        current_time: moment().format(),
      });
      return;
    }

    const profilePic = profileDetails?.profile?.profile?.profile_pic;
    if (profilePic && profilePicFileName !== profilePic) {
      customConsole("Profile Picture changed!");
      setProfilePicFileName(profilePic);
    }

    if (profilePictPath !== profileDetails?.profile_pic_path) {
      customConsole(
        "profileDetails.profile_pic_pathsss: ",
        profileDetails?.profile_pic_path,
      );
      setProfilePictPath(profileDetails?.profile_pic_path);
    }
  }, [
    handleUpdateProfileDetails,
    isProfilePicUploaded,
    profileDetails,
    profilePicFileName,
    profilePictPath,
  ]);
  useEffect(() => {
    if (profilePicFileName?.length) {
      dispatch(
        HandleApiActions({
          ...proileActions.get_profile_pic,
          show_toast: true,
          file_name: profilePicFileName,
          is_file: true,
        }),
      );
    }
  }, [dispatch, profilePicFileName]);
  const handleOpenProfilePicDialog = () => {
    setOpenProfilePicDialog(true);
  };

  const handleCloseProfilePicDialog = () => {
    setOpenProfilePicDialog(false);
    setSelectedProfilePic(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ["jpg", "jpeg", "png"];

      const maxSize = 800 * 1024;

      if (allowedExtensions.includes(fileExtension) === false) {
        toast.dark("Please select a valid file (JPG or PNG)", {
          type: "error",
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      if (file.size > maxSize) {
        toast.dark("File size should not exceed 800KB", {
          type: "error",
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      setSelectedProfilePic(file);
      setIsUploading(true);
      setUploadProgress(0);

      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }

      uploadIntervalRef.current = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(uploadIntervalRef.current);
            uploadIntervalRef.current = null;
            setIsUploading(false);
            return 100;
          }
          return prev + 10;
        });
      }, 100);
    }
  };

  const handleUpdateProfilePic = () => {
    if (selectedProfilePic) {
      setIsProfilePicUploaded(true);
      dispatch(
        HandleApiActions({
          ...proileActions.upload_profile_pic,
          show_toast: true,
          file: selectedProfilePic,
          is_file: true,
        }),
      );
      handleCloseProfilePicDialog();
    }
  };
  const handleEditNameClick = () => {
    setEditedName(profileDetails?.profile?.fullname || "");
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    if (
      editedName.trim().length >= 3 &&
      editedName.trim() !== (profileDetails?.profile?.fullname || "").trim()
    ) {
      handleUpdateProfileDetails({
        current_time: moment().format(),
        fullname: editedName.trim(),
      });
      setIsEditingName(false);
    }
  };

  const handleCancelNameEdit = () => {
    setIsEditingName(false);
    setEditedName(profileDetails?.profile?.fullname || "");
  };

  const handleEditPhoneClick = () => {
    const phoneCodeStr = profileDetails?.profile?.phone_code || "";
    const phoneNumber = profileDetails?.profile?.phone || "";

    setEditedPhoneCode(phoneCodeStr);
    setEditedPhone(phoneNumber);
    setIsEditingPhone(true);
  };

  const handleSavePhone = () => {
    const originalPhone = (profileDetails?.profile?.phone || "").trim();
    const originalPhoneCode = profileDetails?.profile?.phone_code || "";
    const isUnchanged =
      editedPhone.trim() === originalPhone &&
      editedPhoneCode === originalPhoneCode;

    if (editedPhone.trim().length >= 6 && editedPhoneCode && !isUnchanged) {
      handleUpdateProfileDetails({
        current_time: moment().format(),
        phone: {
          code: editedPhoneCode,
          number: editedPhone.trim(),
        },
      });
      setIsEditingPhone(false);
    }
  };

  const handleCancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setEditedPhone(profileDetails?.profile?.phone || "");
    setEditedPhoneCode(profileDetails?.profile?.phone_code || "");
  };

  const handleEditTimeZoneClick = () => {
    setEditedTimeZone(profileDetails?.profile?.profile?.region || "");
    setIsEditingTimeZone(true);
  };

  const handleSaveTimeZone = () => {
    const originalRegion = (
      profileDetails?.profile?.profile?.region || ""
    ).trim();
    const isUnchanged = editedTimeZone.trim() === originalRegion;
    if (editedTimeZone.trim().length > 0 && !isUnchanged) {
      handleUpdateProfileDetails({
        current_time: moment().format(),
        region: editedTimeZone.trim(),
      });
      setIsEditingTimeZone(false);
    }
  };

  const handleCancelTimeZoneEdit = () => {
    setIsEditingTimeZone(false);
    setEditedTimeZone(profileDetails?.profile?.profile?.region || "");
  };

  const handleGetUserChangeLogs = useCallback(
    async ({ user_id, pageNo = 1, pageSize = 25 }) => {
      const apiDetails = userManagementApis.get_user_change_logs;

      if (user_id === undefined || user_id === null) return;

      customConsole({
        ...apiDetails,
        params: {
          user_id,
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            user_id,
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );
      if (resp.error) {
        setUserChangeLogsList([]);
      } else {
        setUserChangeLogsList(resp?.data || []);
      }
    },
    [dispatch],
  );

  const handleUserLogsDialogOpen = (data) => {
    setOpenUserChangeLogsDialog(true);
    setUserLogsDialogData(data);
    setLogsPage(1);
  };

  const handleUserLogsDialogClose = () => {
    setUserLogsDialogData(null);
    setUserChangeLogsList([]);
    setOpenUserChangeLogsDialog(false);
    setLogsPage(1);
    setLogsDateRange([null, null]);
  };

  useEffect(() => {
    if (openUserChangeLogsDialog && userLogsDialogData?.user_id) {
      handleGetUserChangeLogs({
        user_id: userLogsDialogData.user_id,
        pageNo: logsPage,
        pageSize: logsRowsPerPage,
      });
    }
  }, [
    handleGetUserChangeLogs,
    logsPage,
    logsRowsPerPage,
    openUserChangeLogsDialog,
    userLogsDialogData,
  ]);

  const breadcrumbItems = [{ label: "Settings" }, { label: "Profile" }];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredUserChangeLogs = userChangeLogsList.filter((log) => {
    const [startDate, endDate] = logsDateRange;
    if (startDate || endDate) {
      const logDate = moment(log?.created_at);

      if (startDate && endDate) {
        return (
          logDate.isSameOrAfter(startDate, "day") &&
          logDate.isSameOrBefore(endDate, "day")
        );
      }
      if (startDate) {
        return logDate.isSameOrAfter(startDate, "day");
      }
      return logDate.isSameOrBefore(endDate, "day");
    }

    return true;
  });

  return (
    <Box sx={{ padding: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, textAlign: "left" }}>
          <CommonBreadcrumbs items={breadcrumbItems} />
        </Box>

        <Box>
          <Button
            variant="outlined"
            sx={{
              fontStyle: "Open Sans",
              fontSize: "14px",
              color: "#26890D",
              borderColor: "#26890D",
              "&:hover": {
                backgroundColor: "#E8F5E8",
                borderColor: "#26890D",
              },
              textTransform: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleUserLogsDialogOpen({
                user_id: profileDetails?.profile?.user_id,
                fullname: profileDetails?.profile?.fullname,
                email: profileDetails?.profile?.email,
              });
            }}
          >
            User Logs
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ position: "relative", mb: 2 }}>
          <Avatar
            sx={{
              width: 128,
              height: 128,
              bgcolor: "#E0E0E0",
              border: "1px solid #ddd",
            }}
            src={profilePictPath || DefaultProfilePic}
          />
          <IconButton
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #ddd",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            onClick={handleOpenProfilePicDialog}
          >
            <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
          </IconButton>
        </Box>
        <Typography
          sx={{
            fontStyle: "Manrope",
            fontSize: "22px",
            fontWeight: "bold",
            lineHeight: "28px",
          }}
        >
          {profileDetails?.profile?.fullname || "User Name"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontStyle: "Manrope",
            fontSize: "16px",
            fontWeight: "regular",
            color: "#758763",
            lineHeight: "24px",
          }}
        >
          {profileDetails?.profile?.role_details?.role_name || "Role"}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{
            sx: {
              backgroundColor: "#26890D", // Green indicator line
              height: 2,
            },
          }}
        >
          <Tab
            label="Details"
            sx={{
              fontStyle: "Open Sans",
              fontSize: "14px",
              fontWeight: "regular",
              lineHeight: "20px",
              textTransform: "none",
              color: tabValue === 0 ? "#000000" : "#666666", // Black when selected, gray when not
              "&.Mui-selected": {
                color: "#000000", // Ensure black color when selected
              },
            }}
          />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                mb: 2,
                boxShadow: 1,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Full Name
                  </Typography>
                  {isViewingName ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton size="small" onClick={handleEditNameClick}>
                        <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleSaveName}
                        disabled={
                          editedName.trim().length < 3 ||
                          editedName.trim() ===
                            (profileDetails?.profile?.fullname || "").trim()
                        }
                        sx={{
                          color:
                            editedName.trim().length < 3 ||
                            editedName.trim() ===
                              (profileDetails?.profile?.fullname || "").trim()
                              ? "#ccc"
                              : "#000",
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelNameEdit}
                        sx={{ color: "#000" }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {isViewingName ? (
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "regular",
                      fontSize: "14px",
                      lineHeight: "21px",
                      textAlign: "left",
                      color: "#758763",
                      letterSpacing: "0px",
                    }}
                  >
                    {profileDetails?.profile?.fullname || "-"}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {editedName.trim().length > 0 &&
                      editedName.trim().length < 3 && (
                        <Box
                          sx={{
                            ...componentStyle.font.form.textFieldError,
                            fontWeight: 500,
                            color: "#6f7174",
                          }}
                        >
                          Minimum{" "}
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            3
                          </span>{" "}
                          characters required
                        </Box>
                      )}
                    {editedName.length >= 35 && (
                      <Box
                        sx={{
                          ...componentStyle.font.form.textFieldError,
                          fontWeight: 500,
                          color: "#758763",
                        }}
                      >
                        Maximum{" "}
                        <span
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          35
                        </span>{" "}
                        characters allowed
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      value={editedName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 35) {
                          setEditedName(value);
                        }
                      }}
                      inputProps={{ maxLength: 35 }}
                      variant="outlined"
                      size="small"
                      autoFocus
                      error={
                        (editedName.trim().length > 0 &&
                          editedName.trim().length < 3) ||
                        editedName.length >= 35
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "14px",
                          "& fieldset": {
                            borderColor: "#000000",
                            borderWidth: "1px",
                            borderRadius: "0px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#000000",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#000000",
                          },
                          "&.Mui-error fieldset": {
                            borderColor: "#000000",
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                mb: 2,
                boxShadow: 1,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Role
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontStyle: "Manrope",
                    fontWeight: "regular",
                    fontSize: "14px",
                    lineHeight: "21px",
                    textAlign: "left",
                    color: "#758763",
                    letterSpacing: "0px",
                  }}
                >
                  {profileDetails?.profile?.role_details?.role_name || "-"}
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                boxShadow: 1,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Time Zone
                  </Typography>
                  {isViewingTimeZone ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleEditTimeZoneClick}
                      >
                        <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleSaveTimeZone}
                        disabled={
                          editedTimeZone.trim().length === 0 ||
                          editedTimeZone.trim() ===
                            (
                              profileDetails?.profile?.profile?.region || ""
                            ).trim()
                        }
                        sx={{
                          color:
                            editedTimeZone.trim().length === 0 ||
                            editedTimeZone.trim() ===
                              (
                                profileDetails?.profile?.profile?.region || ""
                              ).trim()
                              ? "#ccc"
                              : "#000",
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelTimeZoneEdit}
                        sx={{ color: "#000" }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {isEditingTimeZone ? (
                  <Select
                    fullWidth
                    size="small"
                    value={editedTimeZone}
                    onChange={(e) => setEditedTimeZone(e.target.value)}
                    autoFocus
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                        sx: {
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#f1f1f1",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#888",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "#555",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      fontSize: "14px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#000000",
                        borderWidth: "1px",
                        borderRadius: "0px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#000000",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#000000",
                      },
                      textAlign: "left",
                    }}
                  >
                    {countryRegionsStore.countryAndPhoneCode?.length
                      ? countryRegionsStore.countryAndPhoneCode
                          .map((data) => data?.time_zone)
                          .sort((a, b) => a.localeCompare(b))
                          .map((region) => (
                            <MenuItem
                              key={region}
                              value={region}
                              sx={{ fontSize: "14px" }}
                            >
                              {region}
                            </MenuItem>
                          ))
                      : null}
                  </Select>
                ) : (
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "regular",
                      fontSize: "14px",
                      lineHeight: "21px",
                      textAlign: "left",
                      color: "#758763",
                      letterSpacing: "0px",
                    }}
                  >
                    {profileDetails?.profile?.profile?.region || "ASIA/DELHI"}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                mb: 2,
                boxShadow: 1,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Phone Number
                  </Typography>
                  {isViewingPhone ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton size="small" onClick={handleEditPhoneClick}>
                        <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        minWidth: 72,
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleSavePhone}
                        disabled={
                          editedPhone.trim().length < 8 ||
                          !editedPhoneCode ||
                          ((profileDetails?.profile?.phone_code || "") ===
                            editedPhoneCode &&
                            (profileDetails?.profile?.phone || "").trim() ===
                              editedPhone.trim())
                        }
                        sx={{
                          color:
                            editedPhone.trim().length < 8 ||
                            !editedPhoneCode ||
                            ((profileDetails?.profile?.phone_code || "") ===
                              editedPhoneCode &&
                              (profileDetails?.profile?.phone || "").trim() ===
                                editedPhone.trim())
                              ? "#ccc"
                              : "#000",
                        }}
                      >
                        <SaveIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelPhoneEdit}
                        sx={{ color: "#000" }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {isEditingPhone ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {editedPhone.trim().length > 0 &&
                      editedPhone.trim().length < 8 && (
                        <Box
                          sx={{
                            ...componentStyle.font.form.textFieldError,
                            fontWeight: 500,
                            color: "#6f7174",
                          }}
                        >
                          Minimum{" "}
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            8
                          </span>{" "}
                          numbers required
                        </Box>
                      )}
                    {editedPhone.length >= 15 && (
                      <Box
                        sx={{
                          ...componentStyle.font.form.textFieldError,
                          fontWeight: 500,
                          color: "#758763",
                        }}
                      >
                        Maximum{" "}
                        <span
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          15
                        </span>{" "}
                        numbers reached
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      <Select
                        value={editedPhoneCode}
                        onChange={(e) => setEditedPhoneCode(e.target.value)}
                        size="small"
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              maxWidth: 220,
                            },
                            sx: {
                              "&::-webkit-scrollbar": {
                                width: "8px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "#f1f1f1",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px",
                                "&:hover": {
                                  backgroundColor: "#555",
                                },
                              },
                            },
                          },
                        }}
                        sx={{
                          flex: 1,
                          fontSize: "14px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                            borderWidth: "1px",
                            borderRadius: "0px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000000",
                          },
                        }}
                      >
                        {countryRegionsStore.countryAndPhoneCode?.length
                          ? countryRegionsStore.countryAndPhoneCode
                              .sort((a, b) =>
                                a.country.localeCompare(b.country),
                              )
                              .map((data) => (
                                <MenuItem
                                  key={`${data.country}-${data.phone_code}`}
                                  value={data.phone_code}
                                  sx={{ fontSize: "14px" }}
                                >
                                  {data.country} {data.phone_code}
                                </MenuItem>
                              ))
                          : null}
                      </Select>
                      <TextField
                        fullWidth
                        type="text"
                        inputMode="numeric"
                        value={editedPhone}
                        onChange={(e) => {
                          const value = e.target.value.replaceAll(/\D/g, "");
                          if (value.length <= 15) {
                            setEditedPhone(value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key.length === 1 && /\d/.test(e.key) === false) {
                            e.preventDefault();
                          }
                        }}
                        variant="outlined"
                        size="small"
                        autoFocus
                        error={
                          editedPhone.trim().length > 0 &&
                          editedPhone.trim().length < 8
                        }
                        inputProps={{ maxLength: 15 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "14px",
                            "& fieldset": {
                              borderColor: "#000000",
                              borderWidth: "1px",
                              borderRadius: "0px",
                            },
                            "&:hover fieldset": {
                              borderColor: "#000000",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#000000",
                            },
                            "&.Mui-error fieldset": {
                              borderColor: "#000000",
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "regular",
                      fontSize: "14px",
                      lineHeight: "21px",
                      textAlign: "left",
                      color: "#758763",
                      letterSpacing: "0px",
                    }}
                  >
                    {profileDetails?.profile?.phone_code && profileDetails?.profile?.phone
                      ? `${profileDetails?.profile?.phone_code}-${profileDetails?.profile?.phone}`
                      : "-"}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                mb: 2,
                boxShadow: 1,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Department
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontStyle: "Manrope",
                    fontWeight: "regular",
                    fontSize: "14px",
                    lineHeight: "21px",
                    textAlign: "left",
                    color: "#758763",
                    letterSpacing: "0px",
                  }}
                >
                  {profileDetails?.profile?.role_details?.department ||
                    "DELOITTE"}
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                boxShadow: 1,
                mb: 5,
                minHeight: 110,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontStyle: "Manrope",
                      fontWeight: "bold",
                      fontSize: "16px",
                      lineHeight: "20px",
                    }}
                  >
                    Email Address
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontStyle: "Manrope",
                    fontWeight: "regular",
                    fontSize: "14px",
                    lineHeight: "21px",
                    textAlign: "left",
                    color: "#758763",
                    letterSpacing: "0px",
                  }}
                >
                  {profileDetails?.profile?.email || "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Box>
          <Typography variant="h6">User Access Information</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
            User access permissions and settings will be displayed here.
          </Typography>
        </Box>
      )}
      <CustomDialogBoxV2
        open={openProfilePicDialog}
        onClose={handleCloseProfilePicDialog}
        title="Update Profile Picture"
        actionButtonStyle="continuous"
        successButtonName="Update"
        rejectButtonName="Close"
        onClickSuccess={handleUpdateProfilePic}
        onClickReject={handleCloseProfilePicDialog}
        onDisabled={!selectedProfilePic}
      >
        <Box sx={{ minWidth: "400px", padding: "4px" }}>
          <Typography sx={{ marginBottom: "8px" }}>
            Upload File<span style={{ color: "red" }}>*</span>
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#666",
              marginBottom: "16px",
            }}
          >
            JPG or PNG. Max size of 800K
          </Typography>
          <Button
            size="small"
            variant="contained"
            component="label"
            sx={{
              backgroundColor: "#26890D",
              color: "white",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1f6e0a",
              },
              borderRadius: "0px",
            }}
          >
            <span>Choose File</span>

            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handleFileSelect}
            />
          </Button>

          {selectedProfilePic && (
            <Box sx={{ marginTop: "16px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "0px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#53565A",
                  }}
                >
                  {selectedProfilePic.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedProfilePic(null);
                    setUploadProgress(0);
                    setIsUploading(false);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {selectedProfilePic && (
                <Box sx={{ width: "100%", marginBottom: "8px" }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: "5px",
                      borderRadius: "0px",
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#26890D",
                        borderRadius: "0px",
                      },
                    }}
                  />
                </Box>
              )}

              {isUploading === false && uploadProgress === 100 && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#26890D",
                  }}
                >
                  Successfully Uploaded
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={openUserChangeLogsDialog}
        onClose={handleUserLogsDialogClose}
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
              User Logs - Name: {userLogsDialogData?.fullname || ""}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 4 }}>
              <Typography sx={{ fontSize: "14px", color: "#53565A" }}>
                Select a Date
              </Typography>
              <TextField
                size="small"
                placeholder="MM/DD/YYYY - MM/DD/YYYY"
                value={
                  logsDateRange[0] && logsDateRange[1]
                    ? `${logsDateRange[0].format(
                        "MM/DD/YYYY",
                      )} - ${logsDateRange[1].format("MM/DD/YYYY")}`
                    : ""
                }
                onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {(logsDateRange[0] || logsDateRange[1]) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLogsDateRange([null, null]);
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16, color: "#000" }} />
                        </IconButton>
                      )}
                      <CalendarMonthIcon
                        sx={{ fontSize: 18, color: "#000", cursor: "pointer" }}
                      />
                    </Box>
                  ),
                }}
                sx={{
                  width: "235px",
                  cursor: "pointer",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13px",
                    cursor: "pointer",
                    "& fieldset": {
                      borderColor: "#000000",
                      borderRadius: "0px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#000000",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                }}
              />
              <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={() => setDatePickerAnchor(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      overflow: "visible",
                      borderRadius: "0px",
                    },
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Box sx={{ display: "flex", gap: 0, padding: 0 }}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={logsDateRange[0]}
                      onChange={(newValue) => {
                        setLogsDateRange([newValue, logsDateRange[1]]);
                        if (newValue && logsDateRange[1]) {
                          setDatePickerAnchor(null);
                        }
                      }}
                      maxDate={logsDateRange[1]}
                      slotProps={{
                        actionBar: { actions: [] },
                      }}
                      sx={compactDatePickerSx}
                    />
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mt: "16px", mb: 0 }}
                    />
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={logsDateRange[1]}
                      onChange={(newValue) => {
                        setLogsDateRange([logsDateRange[0], newValue]);
                        if (logsDateRange[0] && newValue) {
                          setDatePickerAnchor(null);
                        }
                      }}
                      minDate={logsDateRange[0]}
                      slotProps={{
                        actionBar: { actions: [] },
                      }}
                      sx={compactDatePickerSx}
                    />
                  </Box>
                </LocalizationProvider>
              </Popover>
            </Box>
          </Box>
        }
        paperSx={{
          width: "min(760px, 85vw)",
        }}
        contentSx={{
          p: 0,
          overflow: "hidden",
          height: "min(420px, calc(100vh - 200px))",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            padding: "16px",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          {filteredUserChangeLogs?.length ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1.5px solid #c7c7c7ff",
                  padding: "12px 16px",
                }}
              >
                <Typography
                  sx={{
                    flex: 2,
                    fontSize: "14px",
                    fontStyle: "Open Sans",
                    color: "#53565A",
                    textAlign: "left",
                  }}
                >
                  Changes
                </Typography>
                <Typography
                  sx={{
                    flex: 2,
                    fontSize: "14px",
                    fontStyle: "Open Sans",
                    color: "#53565A",
                    textAlign: "left",
                  }}
                >
                  Changed By
                </Typography>
                <Typography
                  sx={{
                    flex: 1.5,
                    fontSize: "14px",
                    fontStyle: "Open Sans",
                    color: "#53565A",
                    textAlign: "left",
                  }}
                >
                  Time Stamp
                </Typography>
              </Box>

              {filteredUserChangeLogs.map((log, index) => (
                <Box
                  key={`${log?.created_at || "na"}-${log?.changed_by?.email || "na"}-${index}`}
                  sx={{
                    display: "flex",
                    padding: "12px 16px",
                    alignItems: "flex-start",
                    borderBottom:
                      index === filteredUserChangeLogs.length - 1
                        ? "none"
                        : "1.5px solid #c7c7c7ff",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 2,
                      fontSize: "14px",
                      color: "#000000",
                      textAlign: "left",
                      wordBreak: "break-word",
                      paddingRight: "8px",
                    }}
                  >
                    {JSON.stringify(log?.current_changes)}
                  </Typography>
                  <Typography
                    sx={{
                      flex: 2,
                      fontSize: "14px",
                      color: "#000000",
                      textAlign: "left",
                      wordBreak: "break-word",
                      paddingRight: "8px",
                    }}
                  >
                    <Box>{log?.changed_by?.name},</Box>
                    <Box>{log?.changed_by?.email}</Box>
                  </Typography>
                  <Typography
                    sx={{
                      flex: 1.5,
                      fontSize: "14px",
                      color: "#000000",
                      textAlign: "left",
                      wordBreak: "break-word",
                      paddingRight: "8px",
                    }}
                  >
                    {convertUtcToTimeZone(
                      log?.created_at,
                      profileDetails?.profile?.profile?.region,
                    )}
                  </Typography>
                </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                width: "100%",
                flex: 1,
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>No logs found</Typography>
            </Box>
          )}
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default ProfileConfig;
